/* eslint-disable max-len */
import styled, { createGlobalStyle, css } from 'styled-components';
import { Wrapper as DayWrapper } from '@widgets/GenericBookings/components/CustomDatepicker/components/DatepickerDay/styles';
import { datepicker, datepickerDayName, datepickerDayNames, datepickerHeader, datepickerMonth, datepickerMonthContainer, datepickerWeek, datepickerWrapper, datepickerDay, dayWrapper, dayOutsideMonth, dayDisabled, monthText, inputContainer, decemberMonth, } from '@widgets/shared/components/CustomDatepicker/styles/defaults';
import MediaSizes from '@widgets/shared/MediaSizes';
// timeline positioning and animation variables
export const swwCalendarWidth = 260; // required to calculate position of the months relative to the calendar
export const swwCalendarPadding = 20; // just in case
export const swwCalendarWidthWithPadding = swwCalendarWidth + swwCalendarPadding; // calendar size with padding, need for positioning to calendar
export const swwMonthWidth = 100; // so can correctly center date relative to calendar
export const swwMonthGutter = 35; // amount to subtract on none active months so it's not offset by 100 each month.
export const CalendarKeyWrapper = styled.div `
  ${({ theme: { calendarKeyWrapper } }) => calendarKeyWrapper}
`;
export const CalendarWrapper = styled.div `
  ${({ theme: { calendarWrapper } }) => calendarWrapper}
  
  ${(props) => css `
    @media screen and (max-width: ${MediaSizes.XS_MAX}px) {
      .react-datepicker__month-container:nth-of-type(${props.selectedIdx}) {
        & ~ .react-datepicker__month-container {
          animation: wiggle;
          animation-duration: 3s;
          animation-iteration-count: infinite;
          animation-delay: 3s;
          animation-timing-function: ease-out;
        }
      }
    }
  `}
`;
export const Timeline = styled.ul `
  list-style: none;
  display: flex;
  z-index: 2;
  transition: 200ms ease-out transform;
  margin: 0;
  // size of number of calendars available, in case we need to re-use this with fewer or more calendars in the future
  width: ${(props) => props.monthCount * swwCalendarWidthWithPadding}px;
  position: relative;
  
  // 8.5rem is size inclusive of 40px bottom padding...
  padding: 0 0 8.5rem;
  
  &::after {
    content: "";
    display: block;
    position: absolute;
    bottom: 2.5rem; // inline with middle of circle
    
    // custom dotted line
    background-image: repeating-linear-gradient(90deg, #00346A, #00346A 2rem, transparent 2rem, transparent 3.2rem);
    background-position: 0.4rem bottom;
    background-repeat: repeat-x;
    background-size: 100% 0.7rem;
    height: 0.7rem;
    width: 100%;
  }
`;
export const TimelineItem = styled.li `
  // forcing to 100px so we don't have any complications with larger months
  display: flex;
  gap: 1.4rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 10rem;
  padding: 0;
  z-index: 2;
  font: var(--heading-four-font);
  text-align: center;
  position: absolute;
  top: 0;
  transition: 200ms ease-out transform;

  // the dot that appears below the month name
  &::after {
    content: "";
    display: block;
    position: initial;
    z-index: 2;
    background-color: var(--primary-dark);
    border: 0.3rem solid white;
    border-radius: 50%;
    width: 1.6rem;
    height: 1.6rem;
  }
  
  &:first-of-type {
    &::before {
      content: "";
      position: absolute;
      
      // hide the dotted line before the first dot.
      right: calc(100% - ${swwMonthWidth / 2}px);
      background-color: var(--primary-dark);
      width: 100vw;
      height: 0.7rem;
      bottom: 0.4rem; // amount needed to hide the dotted line.
    }
  }
  
  &:last-of-type {
    &::before {
      content: "";
      position: absolute;
      // hide the dotted line after the last dot.
      left: calc(100% - ${swwMonthWidth / 2}px);
      background-color: var(--primary-dark);
      width: 100vw;
      height: 0.7rem;
      bottom: 0.4rem; // amount needed to hide the dotted line.
    }
  }
  
  ${({ active, idx }) => {
    if (active) {
        return css `

        // anchor to the middle of the corresponding calendar, this one shouldn't really occur but this is in case logic needs to change
        ${idx !== 0 && css `
          transform: translateX(${(idx * swwCalendarWidthWithPadding) + (swwCalendarWidth / 2) - (swwMonthWidth / 2)}px);
        `}
        
        // anchor to the middle on the first calendar
        ${idx === 0 && css `
          transform: translateX(${(swwCalendarWidth / 2) - (swwMonthWidth / 2)}px);
        `}
        
        &::after {
          transition: transform ease-out 200ms;
          transform: scale(1.75);
        }`;
    }
    return css `
      // anchor to the beginning of the corresponding calendar
      ${idx !== 0 && css `
        transform: translateX(${(idx * swwCalendarWidthWithPadding) - swwMonthGutter}px);
      `}
    `;
}}
`;
export const DatePickerStyles = createGlobalStyle `
  ${({ id, theme: { defaults, calendarDay, calendarDayBox, calendarDOW, calendarDays, calendarDayOutside, calendarHeader, monthContainer, }, }) => css `
      #${id} {
        .react-datepicker-wrapper {
          ${datepickerWrapper}
        }
  
        .react-datepicker {
          ${datepicker}
          ${defaults}
  
          .react-datepicker__header {
            ${datepickerHeader}
            ${calendarHeader}
  
            .react-datepicker__day-names {
              ${datepickerDayNames}
              ${calendarDays}
  
              .react-datepicker__day-name {
                ${datepickerDayName}
                ${calendarDOW}
              }
            }
          }
          
  
          .react-datepicker__month-container {
            ${datepickerMonthContainer}
  
            .react-datepicker__month {
              ${datepickerMonth}
  
              .react-datepicker__week {
                ${datepickerWeek}
  
                .react-datepicker__day {
                  ${datepickerDay}
                  ${calendarDay}
                  ${calendarDayBox}
                  
                  ${DayWrapper} {
                    ${dayWrapper}
                  }
                  
                  &--outside-month {
                    ${dayOutsideMonth};
                    ${calendarDayOutside}
                  }
  
                  &--disabled {
                    ${dayDisabled};
                  }
                }
              }
  
              .react-datepicker__month-text,
              .react-datepicker__quarter-text {
                ${monthText}
              }
            }
          }
  
          ${decemberMonth}
          
        }
  
        .react-datepicker__day-names,
        .react-datepicker__week {
          white-space: nowrap;
        }
        
        .react-datepicker__month-container {
          ${monthContainer};
        }
  
        .react-datepicker__input-container {
          ${inputContainer}
        }
      }
    `}}
`;
